import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/auth/useAuth';

/**
 * Guards a route, remembering where the person was headed so sign-in can return them.
 *
 * It waits on `isInitializing` from AuthContext — the one-time session bootstrap — and
 * NOT on authStore's isLoading, which AuthProvider never sets during that bootstrap.
 * Reading the wrong flag meant that on every reload the user was briefly null, so a
 * signed-in person was redirected to sign-in before their session finished restoring.
 * Caught by the end-to-end reload test, not by any unit test.
 */
export default function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isInitializing } = useAuth();
  const location = useLocation();

  if (isInitializing) return null;
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}
